import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getOperatorLabel, getOperatorDisplayText } from '../../utils/query/operatorLabels';
import styles from '../../styles/query/OperatorSelector.module.scss';

/**
 * Operator Selector Component (JIRA-style)
 * 
 * Dropdown for selecting operators from field metadata
 * Shows all available operators for the field
 * 
 * @param {Object} props
 * @param {string} props.operator - Current operator
 * @param {Array} props.availableOperators - List of available operators from field metadata
 * @param {string} props.fieldLabel - Field label for display
 * @param {function} props.onOperatorChange - Handler for operator change
 */
export function OperatorSelector({ 
  operator = '=', 
  availableOperators = ['=', '!='], 
  fieldLabel = '',
  onOperatorChange 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Calculate dropdown position
  useEffect(() => {
    if (buttonRef.current && isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      // Viewport dimensions
      const viewportWidth = window.innerWidth;
      
      setDropdownPosition({
        top: rect.bottom + scrollTop + 4,
        left: rect.left + scrollLeft,
        width: Math.max(rect.width, 200)
      });
    }
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleOperatorSelect = (e, newOperator) => {
    e.preventDefault();
    e.stopPropagation();
    if (onOperatorChange) {
      console.log('OperatorSelector: Changing operator to', newOperator);
      onOperatorChange(newOperator);
    }
    setIsOpen(false);
  };

  const currentLabel = getOperatorLabel(operator);
  const displayText = fieldLabel 
    ? `${fieldLabel} ${operator} (${currentLabel})`
    : `${operator} (${currentLabel})`;

  // Filter available operators (fallback to common operators if not provided)
  const operators = availableOperators && availableOperators.length > 0 
    ? availableOperators 
    : ['=', '!=', 'contains', 'in'];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.operatorButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className={styles.operatorLabel}>{displayText}</span>
        <ChevronDownIcon className={styles.chevronIcon} />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={styles.operatorDropdown}
          data-operator-dropdown="true"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 100
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {operators.map((op) => (
            <button
              key={op}
              type="button"
              className={`${styles.operatorOption} ${operator === op ? styles.selected : ''}`}
              onClick={(e) => handleOperatorSelect(e, op)}
              onMouseDown={(e) => e.preventDefault()} // Prevent focus issues
            >
              <span className={styles.operatorSymbol}>{op}</span>
              <span className={styles.operatorName}>{getOperatorLabel(op)}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

export default OperatorSelector;

